// AiSuggestedReports.jsx
import React, { useState, useEffect } from "react";
import ProgressBar from "../progressbar/ProgressBar";
import OnBoardingContainer from "./onBoardingContainer";
import Logo from "../logo/Logo";
import { Link } from "react-router-dom";
import { FaArrowRight, FaExchangeAlt, FaShare } from "react-icons/fa";
import Loading from "../preloader/Loading";
import AiReportCard from "./AiReportCard";
import { fetchSuggestedReports } from "../../data/suggestedReportsData";
import StorageServices from "../../services/StorageServices";

export default function AiSuggestedReports() {
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [startIndex, setStartIndex] = useState(0);
  const [groupByDept, setGroupByDept] = useState(false);
  const batchSize = 3;

  // Fetch industry and departments from storage
  let attributes = {};
  try {
    const attrStr = StorageServices.get("TD-Active-Team-Attr");
    // console.log("------- Attribute Str:   ", attrStr);
    attributes = attrStr ? JSON.parse(attrStr) : {};
  } catch (e) {
    console.error(e);
  }
  //
  const industry = attributes?.industry || "E-Commerce"; // Fallback to "E-Commerce"
  // Handle departments as a string, null, or empty
  let departments = attributes?.departments;
  if (typeof departments === "string") {
    departments = departments
      .split(",")
      .map((dept) => dept.trim())
      .map((dept) => dept.replace(/[\[\]]/g, "")); // Convert comma-separated string to array
  }
  //
  if (!departments || departments.length === 0) {
    departments = ["sales", "marketing", "operations"]; // Fallback if null, undefined, or empty
  }

  // Fetch initial reports
  useEffect(() => {
    loadMoreReports();
  }, []);

  const loadMoreReports = async () => {
    //
    setIsLoading(true);
    const newReports = await fetchSuggestedReports(
      industry,
      departments,
      startIndex,
      batchSize
    );
    setReports((prev) => [...prev, ...newReports]);
    setStartIndex((prev) => prev + batchSize);
    setIsLoading(false);
  };

  const hasMoreReports = startIndex < departments.length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center py-12">
      <OnBoardingContainer>
        <div className="p-8 lg:p-10">
          {/* Header */}
          <div className="flex justify-between items-center">
            <Logo />
            <ProgressBar level="100%" />
          </div>

          {/* Title */}
          <div className="mt-6">
            <h1 className="text-2xl font-bold text-gray-900">
              You're all set! 🎉
            </h1>
          </div>

          {/* Info Box & CTA */}
          <div className="mt-8 p-5 bg-green-50 border border-green-100 rounded-xl">
            <p className="text-green-800 text-md font-bold">
              Unlock these insights now
            </p>
            <p className="text-green-600 text-sm mt-1">
              Connect your database to transform your data into actionable
              insights.
            </p>
            <div className="mt-4 flex justify-end">
              <Link
                to="/datasources/new"
                className="inline-flex items-center focus:outline-none gap-2 px-5 py-3 text-sm font-medium text-white bg-green-600 rounded-full hover:bg-green-700 transition-colors"
              >
                Connect Database
                <FaArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <div className="mt-6">
            <p className="text-sm text-gray-600 mt-2">
              Here are AI-suggested dashboards tailored to your industry and
              team.
            </p>
          </div>

          {/* Reports Grid */}
          <div className="mt-6 grid gap-4">
            <div className="grid gap-4 md:grid-cols-2">
              {reports.map((report, idx) => (
                <AiReportCard key={idx} {...report} report={report} />
              ))}
            </div>
          </div>

          {/* Loading State */}
          <div className="mt-6">
            <Loading
              loading={isLoading}
              message="Getting your AI suggested reports..."
            />
          </div>

          {/* Load More Button */}
          {!isLoading && hasMoreReports && (
            <div className="mt-6 text-center">
              <button
                onClick={loadMoreReports}
                className="inline-flex items-center px-4 py-2 focus:outline-none text-sm font-medium text-green-600 bg-green-50 rounded-full hover:bg-green-100 transition-colors"
              >
                Load More
                <FaArrowRight className="ml-2 h-4 w-4" />
              </button>
            </div>
          )}

          <div className="mt-4 flex justify-end">
            <Link
              to="/dashboard"
              className="flex gap-1 items-center text-sm text-green-600 hover:text-green-800 font-medium focus:outline-none"
            >
              <FaShare />
              <span>Go to Dashboard</span>
            </Link>
          </div>
        </div>
      </OnBoardingContainer>
    </div>
  );
}
